import { useCallback, useState } from 'react'
import {
  ApiErrorResponse,
  getAppClient,
  handleError,
} from '../../../utils/network_util'
import Enterprise from '../../../model/Enterprise'
import { useSnackBar } from '../../../providers/snack_bar_provider'

type Handler = {
  load: (id: string, cursor?: number) => Promise<void>
  follow: (id: string, set: boolean) => Promise<void>
}

const useEnterpriseFollows = (
  getLimit = 20
): [Array<Enterprise>, number, number, boolean, Handler] => {
  const apiClient = getAppClient()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [totalSize, setTotalSize] = useState(0)
  const [apiCursor, setApiCursor] = useState(0)
  const [followers, setFollows] = useState<Array<Enterprise>>([])
  const { showSnackBar } = useSnackBar()

  const load = useCallback(
    async (id: string, cursor = 0) => {
      if (cursor < 0 || (totalSize && cursor >= totalSize)) {
        return
      }

      setIsLoading(true)
      await apiClient.users
        .enterpriseFollowsDetail(id, { cursor: cursor, limit: getLimit })
        .then((res) => {
          const result = res.data
          if (totalSize !== result.total) {
            setTotalSize(result.total)
          }
          if (result.data) {
            setApiCursor(cursor)
            setFollows(result.data)
          }
        })
        .catch((e: ApiErrorResponse) => {
          handleError(e, showSnackBar)
          return
        })
        .finally(() => setIsLoading(false))
    },
    [totalSize, apiCursor, followers]
  )

  const follow = useCallback(
    async (id: string, set: boolean) => {
      await apiClient.follow
        .userCreate({
          id: id,
          set: set,
        })
        .catch((e: ApiErrorResponse) => {
          handleError(e, showSnackBar)
          return
        })
    },
    [followers]
  )

  return [
    followers,
    totalSize,
    apiCursor,
    isLoading,
    { load: load, follow: follow },
  ]
}

export default useEnterpriseFollows
